import { UIState } from "@hydra/core/uiState";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class Header implements Plugin {
  private unsubscribeSidebar?: () => void;
  private unsubscribeNavbar?: () => void;
  private unsubscribeHeaderSticked?: () => void;
  private unsubscribeCurrentBreakpoint?: () => void;
  private readonly locationChangeHandler = () => this.updateActiveTabsByUrl();

  private readonly menuButton?: HTMLButtonElement;
  private readonly menuOpenIcon?: HTMLElement;
  private readonly menuCloseIcon?: HTMLElement;
  private readonly menuToggleButtons: HTMLElement[] = [];
  private readonly closeButton?: HTMLButtonElement;
  private readonly mobileNavContainer?: HTMLElement;
  private readonly desktopNavActions?: HTMLElement;

  private readonly navWrapper?: HTMLDivElement;
  private readonly headerNav?: HTMLUListElement;
  private readonly headerNavLogo?: HTMLLIElement;
  private readonly headerNavBottom?: HTMLDivElement;
  private readonly menuItems: HTMLLIElement[] = [];
  private readonly loginLinks: HTMLElement[] = [];
  private readonly loginSubmenus: HTMLElement[] = [];
  private readonly headerBottomNavTabs?: HTMLElement;

  private readonly handleMenuClick = () => {
    const uiState = UIState.getInstance();
    uiState.isSidebarOpen = !uiState.isSidebarOpen;
    uiState.isNavbarVisible = !uiState.isNavbarVisible;
  };

  private readonly handleCloseClick = () => {
    const uiState = UIState.getInstance();
    uiState.isSidebarOpen = false;
    uiState.isNavbarVisible = false;
  };

  constructor(private readonly element: HTMLElement) {
    this.menuButton = this.element.querySelector(
      ".dsc-co-header__menu-button",
    ) as HTMLButtonElement;
    this.menuOpenIcon = this.element.querySelector(
      ".dsc-co-header__menu-icon--menu",
    ) as HTMLElement;
    this.menuCloseIcon = this.element.querySelector(
      ".dsc-co-header__menu-icon--close",
    ) as HTMLElement;
    this.menuToggleButtons = Array.from(
      this.element.querySelectorAll(".dsc-plugin-menu-toggle-trigger"),
    );
    this.closeButton = this.element.querySelector(
      ".dsc-co-header-nav-actions__close",
    ) as HTMLButtonElement;
    this.mobileNavContainer = this.element.querySelector(
      ".dsc-co-header-nav-actions--mobile",
    ) as HTMLElement;
    this.desktopNavActions = this.element.querySelector(
      ".dsc-co-header-nav-actions--desktop",
    ) as HTMLElement;
    this.navWrapper = this.element.querySelector(
      ".dsc-co-header__nav-wrapper",
    ) as HTMLDivElement;
    this.headerNav = this.element.querySelector(
      ".dsc-co-header-nav",
    ) as HTMLUListElement;
    this.headerNavLogo = this.element.querySelector(
      ".dsc-co-header-nav__logo",
    ) as HTMLLIElement;
    this.headerNavBottom = this.element.querySelector(
      ".dsc-co-header-nav__bottom",
    ) as HTMLDivElement;
    this.menuItems = Array.from(
      this.element.querySelectorAll(".dsc-co-header-nav__item"),
    );
    this.loginLinks = Array.from(
      this.element.querySelectorAll(".dsc-co-header-nav-actions__login-link"),
    );
    this.loginSubmenus = Array.from(
      this.element.querySelectorAll(
        ".dsc-co-header-nav-actions__login-submenu",
      ),
    );
    this.headerBottomNavTabs = this.element.querySelector(
      ".dsc-co-header-bottom-nav__tabs",
    ) as HTMLElement;
  }

  init(): void {
    const uiState = UIState.getInstance();

    this.update(uiState);

    this.updateActiveTabsByUrl();

    this.unsubscribeSidebar = uiState.subscribe("isSidebarOpen", () =>
      this.update(uiState),
    );
    this.unsubscribeNavbar = uiState.subscribe("isNavbarVisible", () =>
      this.update(uiState),
    );
    this.unsubscribeHeaderSticked = uiState.subscribe("isHeaderSticked", () =>
      this.update(uiState),
    );
    this.unsubscribeCurrentBreakpoint = uiState.subscribe(
      "currentBreakpoint",
      () => this.update(uiState),
    );

    for (const btn of this.menuToggleButtons) {
      btn.addEventListener("click", this.handleMenuClick);
    }
    this.closeButton?.addEventListener("click", this.handleCloseClick);
    for (const link of this.loginLinks) {
      link.addEventListener("click", this.toggleLoginSubmenu);
    }
    document.addEventListener("click", this.handleLoginSubmenuOutsideClick);
  }

  private updateActiveTabsByUrl(): void {
    const {
      href: currentUrl,
      pathname: currentPathname,
      origin,
    } = window.location;
    const normalizedPath = currentPathname.toLowerCase();

    for (const item of this.menuItems) {
      const tab = item.querySelector<HTMLAnchorElement>(".dsc-co-menu-tab");
      if (!tab) continue;

      const anchors = item.querySelectorAll<HTMLAnchorElement>("a[href]");
      const matched = Array.from(anchors).some((anchor) => {
        const href = anchor.getAttribute("href")?.trim();
        if (!href || href.startsWith("#")) return false;

        if (href.startsWith("http")) {
          try {
            const url = new URL(href);
            return (
              url.origin === origin &&
              url.pathname.toLowerCase() === normalizedPath
            );
          } catch {
            return false;
          }
        }

        return href.toLowerCase() === normalizedPath;
      });

      tab.classList.toggle("dsc-co-menu-tab--selected", matched);
      tab.classList.toggle("dsc-co-menu-tab--default", !matched);
    }
  }

  private update(ui: UIState): void {
    const isSidebarOpen = ui.isSidebarOpen;
    const isNavbarVisible = ui.isNavbarVisible;
    const isHeaderSticked = ui.isHeaderSticked;

    this.element.classList.toggle(
      "dsc-co-header-wrapper--sidebar-open",
      isSidebarOpen,
    );

    this.menuButton?.classList.toggle(
      "hidden",
      isNavbarVisible && !isSidebarOpen,
    );

    this.menuOpenIcon?.classList.toggle("hidden", isSidebarOpen);

    this.menuCloseIcon?.classList.toggle("hidden", !isSidebarOpen);

    if (isNavbarVisible) {
      this.navWrapper?.classList.remove("dsc-co-header__nav-wrapper--hidden");
      this.navWrapper?.classList.add("dsc-co-header__nav-wrapper--visible");
    } else {
      this.navWrapper?.classList.remove("dsc-co-header__nav-wrapper--visible");
      this.navWrapper?.classList.add("dsc-co-header__nav-wrapper--hidden");
    }

    this.headerNav?.classList.remove(
      "dsc-co-header-nav--fit",
      "dsc-co-header-nav--col",
      "dsc-co-header-nav--fixed",
      "dsc-co-header-nav--relative",
    );
    this.headerNav?.classList.add(
      isSidebarOpen ? "dsc-co-header-nav--col" : "dsc-co-header-nav--fit",
      isSidebarOpen || !isHeaderSticked
        ? "dsc-co-header-nav--relative"
        : "dsc-co-header-nav--fixed",
    );

    this.headerBottomNavTabs?.classList.toggle(
      "dsc-co-header-bottom-nav__tabs--sidebar-open",
      isSidebarOpen,
    );

    this.headerBottomNavTabs?.classList.toggle(
      "dsc-co-header-bottom-nav__tabs--sidebar-closed",
      !isSidebarOpen,
    );

    this.headerNavLogo?.classList.toggle(
      "dsc-co-header-nav__logo--visible",
      isHeaderSticked,
    );

    this.headerNavBottom?.classList.toggle("hidden", !isSidebarOpen);

    for (const item of this.menuItems) {
      item.classList.toggle("dsc-co-header-nav__item--sidebar", isSidebarOpen);
    }

    if (this.mobileNavContainer) {
      this.mobileNavContainer.classList.toggle("hidden", !ui.isSmallDevice);
      this.mobileNavContainer.classList.toggle("flex", ui.isSmallDevice);
    }

    if (this.desktopNavActions) {
      this.desktopNavActions.classList.toggle("hidden", ui.isSmallDevice);
      this.desktopNavActions.classList.toggle("flex", !ui.isSmallDevice);
    }

    if (this.closeButton) {
      this.closeButton.classList.add("hidden");
      this.closeButton.classList.remove("flex");
    }
  }

  private readonly toggleLoginSubmenu = (event: Event): void => {
    event.preventDefault();

    const clickedLink = event.currentTarget as HTMLElement;
    const container = clickedLink.closest(".dsc-co-header-nav-actions__login");
    const submenu = container?.querySelector<HTMLElement>(
      ".dsc-co-header-nav-actions__login-submenu",
    );

    if (!submenu) return;

    const isVisible = submenu.classList.contains(
      "dsc-co-header-nav-actions__login-submenu--visible",
    );

    // Hide all submenus first
    for (const submenu of this.loginSubmenus) {
      submenu.classList.remove(
        "dsc-co-header-nav-actions__login-submenu--visible",
      );
    }
    for (const link of this.loginLinks) {
      link.setAttribute("aria-expanded", "false");
    }

    // Toggle the clicked one
    if (!isVisible) {
      submenu.classList.add(
        "dsc-co-header-nav-actions__login-submenu--visible",
      );
      clickedLink.setAttribute("aria-expanded", "true");
    }
  };

  private readonly handleLoginSubmenuOutsideClick = (
    event: MouseEvent,
  ): void => {
    const target = event.target as Node;

    const clickedInside = this.loginSubmenus.some((submenu) => {
      const container = submenu.closest(".dsc-co-header-nav-actions__login");
      const link = container?.querySelector(
        ".dsc-co-header-nav-actions__login-link",
      );
      return submenu.contains(target) || link?.contains(target);
    });

    if (!clickedInside) {
      this.hideAllLoginSubmenus();
    }
  };

  private hideAllLoginSubmenus(): void {
    for (const submenu of this.loginSubmenus) {
      submenu.classList.remove(
        "dsc-co-header-nav-actions__login-submenu--visible",
      );
    }
    for (const link of this.loginLinks) {
      link.setAttribute("aria-expanded", "false");
    }
  }

  destroy(): void {
    this.unsubscribeSidebar?.();
    this.unsubscribeNavbar?.();
    this.unsubscribeHeaderSticked?.();
    this.unsubscribeCurrentBreakpoint?.();

    for (const btn of this.menuToggleButtons) {
      btn.removeEventListener("click", this.handleMenuClick);
    }
    this.closeButton?.removeEventListener("click", this.handleCloseClick);
    for (const link of this.loginLinks) {
      link.removeEventListener("click", this.toggleLoginSubmenu);
    }
    document.removeEventListener("click", this.handleLoginSubmenuOutsideClick);

    window.removeEventListener("locationChange", this.locationChangeHandler);
  }
}

PluginRegistry.register("header", Header);
